export function generateId(): string {
  return crypto.randomUUID();
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validateUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function validateSlug(slug: string): boolean {
  const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  return slugRegex.test(slug) && slug.length >= 3 && slug.length <= 30;
}

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

export function escapeHtml(str: string): string {
  if (typeof str !== 'string') {
    return '';
  }
  return str.replace(/[&<>"'`=/]/g, (char) => HTML_ENTITIES[char] || char);
}

export function escapeHtmlWithLineBreaks(str: string): string {
  return escapeHtml(str).replace(/\n/g, '<br>');
}

export function escapeAttribute(str: string): string {
  if (typeof str !== 'string') {
    return '';
  }
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function sanitizeUrl(url: string): string {
  if (typeof url !== 'string') {
    return '';
  }

  const trimmed = url.trim();

  const lowerUrl = trimmed.toLowerCase();
  if (
    lowerUrl.startsWith('javascript:') ||
    lowerUrl.startsWith('vbscript:') ||
    lowerUrl.startsWith('file:') ||
    lowerUrl.startsWith('storage:')
  ) {
    return '';
  }

  if (lowerUrl.startsWith('data:')) {
    if (lowerUrl.startsWith('data:image/')) {
      return trimmed;
    }
    return '';
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return '';
    }
    return trimmed;
  } catch {
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
      return trimmed;
    }
    return '';
  }
}

export function stripHtml(str: string): string {
  if (typeof str !== 'string') {
    return '';
  }
  return str.replace(/<[^>]*>/g, '');
}

export function sanitizeText(str: string, maxLength = 1000): string {
  if (typeof str !== 'string') {
    return '';
  }
  const stripped = stripHtml(str);
  const escaped = escapeHtml(stripped);
  return escaped.slice(0, maxLength);
}

export function html(strings: TemplateStringsArray, ...values: unknown[]): string {
  return strings.reduce((result, str, i) => {
    const value = values[i - 1];
    if (value === undefined || value === null) {
      return result + str;
    }
    if (typeof value === 'object' && value !== null && 'raw' in value) {
      return result + (value as { raw: string }).raw + str;
    }
    return result + escapeHtml(String(value)) + str;
  }, '');
}

export function raw(value: string): { raw: string } {
  return { raw: value };
}

export function attr(value: string | undefined | null): string {
  if (value === undefined || value === null) {
    return '';
  }
  return escapeAttribute(String(value));
}

export function safeUrl(url: string | undefined | null): string {
  if (!url) {
    return '';
  }
  return sanitizeUrl(url);
}

export function hexToContrastColor(hex: string): string {
  let h = hex.replace('#', '');
  if (h.length === 3 || h.length === 4) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  } else if (h.length > 6) {
    h = h.slice(0, 6);
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '#000000';
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
}

export function resolveImageUrl(url: string | undefined | null, apiUrl?: string): string {
  if (!url) {
    return '';
  }

  const trimmed = url.trim();

  if (trimmed.startsWith('storage://')) {
    const key = trimmed.slice(10);
    const base = apiUrl || (import.meta.env.PROD ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:3001'));
    return `${base}/api/storage/${encodeURIComponent(key)}`;
  }

  return sanitizeUrl(trimmed);
}
